import type { MixDecision } from '@sw/engine'
import { computeMix, createMixState, prepareMix } from '@sw/engine'
import type { EmitterRuntime, MixWorld, MixState } from '@sw/engine'
import type { CourseIndex } from '@sw/geo'
import {
  PositionFilter,
  bearingDeg,
  geoToWorld,
  indexCourse,
  pointAtChainage,
  worldHeadingFromTrueNorth,
} from '@sw/geo'
import type { CompiledWorld, EngineState, TickContext } from '@sw/rules'
import { compileWorld, createEngineState, tick } from '@sw/rules'
import type { Action, Emitter, Placement, Pose, Vec2, WorldDefinition } from '@sw/schema'
import { Emitter as EmitterSchema } from '@sw/schema'
import type { AudioRuntime } from '../audio/runtime.js'
import type { BroadcasterInfo } from './live-audio.js'
import { PathRunner } from './paths.js'
import type { PlacementRow, SessionEventView } from '../api/client.js'
import type { SessionSocket } from './socket.js'

export type PositionMode = 'gps' | 'manual'

export interface Diagnostics {
  positionMode: PositionMode
  headingSource: 'compass' | 'courseOverGround' | 'manual' | 'none'
  accuracyM: number | undefined
  accuracyDegraded: boolean
  speedMps: number
  rawPosition: Vec2 | undefined
  filteredPosition: Vec2 | undefined
  headingDeg: number
  activeVoices: number
  culledVoices: number
  rejectedFixes: number
  clockOffsetMs: number
  connected: boolean
  /** True where the creator asked the world to get out of the listener's ears. */
  inSafetyZone: boolean
}

export interface RuntimeEvents {
  /** The words of whatever is speaking, for listeners who need them. */
  onCaption?: (caption: { text: string; emitterId: string } | undefined) => void
  onDiagnostics?: (diagnostics: Diagnostics) => void
  onMix?: (decision: MixDecision) => void
  onLog?: (entry: { ts: number; text: string }) => void
  onCard?: (card: { title: string; body: string; durationMs: number }) => void
  onServerEvent?: (event: SessionEventView) => void
}

/** How often rules are evaluated and the mix recomputed. */
const TICK_MS = 50
/** How often a position is reported to the server. */
const POSE_UPLOAD_MS = 1000

function toPlacement(row: PlacementRow, worldId: string): Placement {
  return {
    id: row.id,
    worldId,
    name: row.name,
    anchor: { lat: row.lat, lon: row.lon },
    rotationDeg: row.rotation_deg,
    scale: row.scale,
    locked: row.locked === 1,
  }
}

/**
 * The player's engine room.
 *
 * Positions come in — from the phone's sensors or from a dot dragged across a
 * map — and are filtered, converted into the world's own frame, and handed to
 * the rule engine and the mixer. Rules the device can decide for itself run
 * here, so a cue fires in milliseconds and keeps firing when the network does
 * not; anything the server owns arrives over the socket and is performed the
 * same way.
 */
export class SessionRuntime {
  private readonly compiled: CompiledWorld
  private readonly engine: EngineState
  private readonly mixWorld: MixWorld
  private readonly paths: PathRunner
  /** One-shots live here so they are spatialised like anything else. */
  private readonly transients: Emitter[] = []
  private lastStepAt = 0
  /**
   * Per-listener bus trims, in decibels.
   *
   * They have to live here rather than being written onto the audio graph:
   * every frame recomputes bus levels from the mix decision, so a gain poked
   * directly onto a node is erased within fifty milliseconds. Holding the trim
   * as an input to that decision is what makes the faders — and a rule that
   * ducks the music bed — actually stick.
   */
  private readonly busTrimDb: Record<string, number> = {}
  private readonly mixState: MixState = createMixState()
  private readonly filter = new PositionFilter()
  private readonly runtimes = new Map<string, EmitterRuntime>()
  private readonly pendingActions: { action: Action; dueAt: number }[] = []
  /** Playlists and random containers waiting to pick their next take. */
  private readonly pendingReplays: { emitterId: string; dueAt: number }[] = []
  private liveEmitterId: string | undefined
  /** Words for each clip, so a spoken world is not closed to people who cannot hear it. */
  private transcripts: ReadonlyMap<string, string> = new Map()
  private captionTimer: number | undefined

  /**
   * Driving the world without walking it.
   *
   * These enter through the same field a satellite fix does, so rules, mix and
   * diagnostics cannot tell the difference — which is the whole point. Poses
   * stay stamped as simulated, so a desk run never counts as a visit.
   */
  private autopilot: { index: CourseIndex; speedMps: number; distanceM: number } | undefined
  private replay: { poses: Pose[]; cursor: number; startedAt: number } | undefined
  private noiseSigmaM = 0

  private placement: Placement | undefined
  private timer: number | undefined
  private geolocationWatch: number | undefined
  private startedAt = 0
  private lastPoseUpload = 0
  private rejectedFixes = 0

  private rawPosition: Vec2 | undefined
  private filteredPosition: Vec2 | undefined
  private headingDeg = 0
  private headingSource: Diagnostics['headingSource'] = 'none'
  private accuracyM: number | undefined
  private speedMps = 0
  private accuracyDegraded = false
  private previousPose: Pose | undefined
  private lastMix: MixDecision | undefined

  positionMode: PositionMode = 'manual'

  constructor(
    private readonly definition: WorldDefinition,
    placementRow: PlacementRow | null,
    private readonly audio: AudioRuntime,
    private readonly socket: SessionSocket | undefined,
    private readonly events: RuntimeEvents = {},
  ) {
    this.compiled = compileWorld(definition)
    this.engine = createEngineState(
      this.compiled.rulesByScope.listener,
      definition.variables,
      'listener',
      { startedAtMs: 0 },
    )
    // Copies, not references: one-shots are appended while a session runs,
    // and moveEmitter and fadeTo mutate what they find here. Sharing those
    // objects with the authored definition would rewrite the world the studio
    // and the map are still rendering from.
    this.mixWorld = {
      ...prepareMix(definition),
      emitters: definition.emitters.map((emitter) => ({ ...emitter })) as Emitter[],
    }
    this.paths = new PathRunner(definition.emitters)
    if (placementRow) this.placement = toPlacement(placementRow, definition.id)

    // Without this a finished narration stays "playing" forever: it keeps a
    // slot in the voice-culling ranking and keeps its bus ducking others.
    audio.setCallbacks({
      onEmitterEnded: (emitterId) => this.onVoiceEnded(emitterId),
    })

    for (const emitter of definition.emitters) {
      this.runtimes.set(emitter.id, { playing: emitter.startBehavior === 'sessionStart' })
      if (emitter.startBehavior === 'sessionStart') audio.play(emitter.id)
    }
  }

  get worldDefinition(): WorldDefinition {
    return this.definition
  }

  get currentPosition(): Vec2 | undefined {
    return this.filteredPosition
  }

  setTranscripts(transcripts: ReadonlyMap<string, string>): void {
    this.transcripts = transcripts
  }

  /**
   * Puts the listener's own music on the Music bus.
   *
   * Their files, hosted for them, played inside the world — which is the only
   * way a browser can duck a running track under a piece of narration, since
   * it cannot touch audio belonging to another app.
   */
  async setPersonalMusic(tracks: { id: string; url: string; bytes: number }[]): Promise<void> {
    this.audio.stop('personal-music', 400)
    this.audio.unregisterEmitter('personal-music')
    this.runtimes.delete('personal-music')

    const emitters = this.mixWorld.emitters as Emitter[]
    const index = emitters.findIndex((candidate) => candidate.id === 'personal-music')
    if (index >= 0) emitters.splice(index, 1)
    if (tracks.length === 0) return

    await this.audio.loadAssets(tracks)
    const emitter = EmitterSchema.parse({
      kind: 'bed',
      id: 'personal-music',
      name: 'Your music',
      source: { kind: 'playlist', assetIds: tracks.map((track) => track.id), order: 'sequential' },
      busId: 'music',
      priority: 10,
    })

    emitters.push(emitter)
    this.audio.registerEmitter(emitter)
    this.runtimes.set('personal-music', { playing: true })
    this.audio.play('personal-music')
  }

  /**
   * Walks a route on its own, at a chosen pace. A tour is meant to be walked,
   * and clicking a map fifty times to check one is not testing it.
   */
  followRoute(points: readonly Vec2[], speedMps: number): void {
    if (points.length < 2) return
    this.positionMode = 'manual'
    this.stopSensors()
    this.replay = undefined
    this.autopilot = {
      index: indexCourse({
        id: 'autopilot',
        name: 'Autopilot',
        points: [...points],
        corridorM: Number.MAX_SAFE_INTEGER,
      }),
      speedMps,
      distanceM: 0,
    }
  }

  stopAutopilot(): void {
    this.autopilot = undefined
  }

  get autopilotProgress(): { distanceM: number; totalM: number } | undefined {
    if (!this.autopilot) return undefined
    return { distanceM: this.autopilot.distanceM, totalM: this.autopilot.index.totalM }
  }

  /**
   * Adds wander to simulated positions.
   *
   * A world that works on a perfectly steady dot and falls apart outdoors has
   * not been tested; this is how the hysteresis and filtering get exercised
   * before anyone puts their shoes on.
   */
  setSimulatedNoise(sigmaM: number): void {
    this.noiseSigmaM = Math.max(0, sigmaM)
  }

  get simulatedNoiseM(): number {
    return this.noiseSigmaM
  }

  /** Re-runs a recorded walk. Turns a report from the field into a repro. */
  replayTrace(poses: readonly Pose[]): void {
    if (poses.length === 0) return
    this.positionMode = 'manual'
    this.stopSensors()
    this.autopilot = undefined
    this.replay = { poses: [...poses], cursor: 0, startedAt: performance.now() - this.startedAt }
  }

  get replaying(): boolean {
    return this.replay !== undefined
  }

  stopReplay(): void {
    this.replay = undefined
  }

  setBusTrim(busId: string, gainDb: number): void {
    this.busTrimDb[busId] = gainDb
  }

  /**
   * Puts a live voice into the world.
   *
   * Represented as an ordinary emitter, so a person speaking from their sofa
   * gets the same distance, direction and ducking as a sound authored into the
   * ground — which is the entire difference between placing somebody at a spot
   * on a map and simply unmuting them. With no pin they are heard everywhere,
   * which is what a race announcer wants.
   */
  setBroadcaster(info: BroadcasterInfo | undefined): void {
    if (!info) {
      this.clearBroadcaster()
      return
    }

    const id = `live-${info.participantId}`
    const emitters = this.mixWorld.emitters as Emitter[]
    const existing = emitters.find((candidate) => candidate.id === id)

    if (existing && this.liveEmitterId === id) {
      // Only the position changes while somebody is talking.
      if (existing.kind === 'point' && info.position) existing.position = info.position
      return
    }

    this.clearBroadcaster()

    const emitter = EmitterSchema.parse(
      info.position
        ? {
            kind: 'point',
            id,
            name: info.name,
            source: { kind: 'asset', assetId: 'live' },
            busId: 'live',
            priority: 95,
            position: info.position,
            distance: { refDistanceM: 15, maxDistanceM: 300 },
          }
        : {
            kind: 'bed',
            id,
            name: info.name,
            source: { kind: 'asset', assetId: 'live' },
            busId: 'live',
            priority: 95,
          },
    )

    emitters.push(emitter)
    this.audio.registerEmitter(emitter)
    this.runtimes.set(id, { playing: true })
    this.liveEmitterId = id
  }

  /** The live stream arrived; hand it to the audio graph. */
  attachLiveStream(participantId: string, stream: MediaStream): void {
    const id = `live-${participantId}`
    if (this.liveEmitterId !== id) return
    this.audio.playStream(id, stream, 'live')
  }

  private clearBroadcaster(): void {
    if (!this.liveEmitterId) return
    const id = this.liveEmitterId
    this.liveEmitterId = undefined
    this.audio.stop(id, 200)
    this.audio.unregisterEmitter(id)
    this.runtimes.delete(id)
    const emitters = this.mixWorld.emitters as Emitter[]
    const index = emitters.findIndex((candidate) => candidate.id === id)
    if (index >= 0) emitters.splice(index, 1)
  }

  /**
   * A voice finished on its own.
   *
   * A single clip is simply over. A playlist moves to the next track, and a
   * random container waits a while and picks another take — that waiting is
   * the whole point of a random container: a bird that calls the moment the
   * last call ends is not a bird.
   */
  private onVoiceEnded(emitterId: string): void {
    const runtime = this.runtimes.get(emitterId)
    if (!runtime?.playing) return

    const emitter = this.mixWorld.emitters.find((candidate) => candidate.id === emitterId)
    const source = emitter?.source
    if (!emitter || !source || source.kind === 'asset') {
      this.runtimes.set(emitterId, { ...runtime, playing: false })
      return
    }

    const wait =
      source.kind === 'playlist'
        ? source.gapMs
        : source.minIntervalMs +
          Math.random() * Math.max(0, source.maxIntervalMs - source.minIntervalMs)

    this.pendingReplays.push({ emitterId, dueAt: Date.now() + wait })
  }

  /** Moves the listener when a simulated source is driving. */
  private advanceSimulation(nowMs: number, deltaSeconds: number): void {
    if (this.replay) {
      const elapsed = nowMs - this.replay.startedAt
      let pose: Pose | undefined
      while (
        this.replay.cursor < this.replay.poses.length &&
        this.replay.poses[this.replay.cursor].t <= elapsed
      ) {
        pose = this.replay.poses[this.replay.cursor]
        this.replay.cursor += 1
      }
      if (pose) {
        this.rawPosition = pose.position
        this.filteredPosition = pose.position
        this.speedMps = pose.speedMps ?? 0
        if (pose.headingDeg !== undefined) this.headingDeg = pose.headingDeg
        this.accuracyM = pose.accuracyM
      }
      if (this.replay.cursor >= this.replay.poses.length) this.replay = undefined
      return
    }

    if (!this.autopilot || deltaSeconds <= 0) return

    const previous = this.filteredPosition
    this.autopilot.distanceM += this.autopilot.speedMps * deltaSeconds
    if (this.autopilot.distanceM >= this.autopilot.index.totalM) {
      this.autopilot.distanceM = this.autopilot.index.totalM
    }

    const truth = pointAtChainage(this.autopilot.index, this.autopilot.distanceM)
    this.rawPosition = truth
    this.filteredPosition = this.withNoise(truth, nowMs)
    this.speedMps = this.autopilot.speedMps
    // Facing where they are going, as a walker would be.
    if (previous) this.headingDeg = bearingDeg(previous, truth)
    if (this.autopilot.distanceM >= this.autopilot.index.totalM) this.autopilot = undefined
  }

  /**
   * Two smooth, incommensurate wobbles rather than white noise: real GPS drifts
   * and wanders back, it does not jump about independently each second, and a
   * filter tuned against the wrong shape of error is tuned against nothing.
   */
  private withNoise(position: Vec2, nowMs: number): Vec2 {
    if (this.noiseSigmaM <= 0) return position
    return {
      x: position.x + Math.sin(nowMs / 3100) * this.noiseSigmaM,
      y: position.y + Math.cos(nowMs / 2300) * this.noiseSigmaM,
    }
  }

  /** Shows the words of a clip that has just started, if it has any. */
  private showCaptionFor(emitterId: string): void {
    const emitter = this.mixWorld.emitters.find((candidate) => candidate.id === emitterId)
    if (emitter?.source.kind !== 'asset') return
    const text = this.transcripts.get(emitter.source.assetId)
    if (!text) return

    window.clearTimeout(this.captionTimer)
    this.events.onCaption?.({ text, emitterId })
    // Roughly reading pace, with a floor so a short line does not flash past.
    const readingMs = Math.max(4000, (text.split(/\s+/).length / 3) * 1000)
    this.captionTimer = window.setTimeout(() => this.events.onCaption?.(undefined), readingMs)
  }

  private drainPendingReplays(): void {
    const now = Date.now()
    for (let i = this.pendingReplays.length - 1; i >= 0; i--) {
      const pending = this.pendingReplays[i]
      if (pending.dueAt > now) continue
      this.pendingReplays.splice(i, 1)
      if (this.runtimes.get(pending.emitterId)?.playing) this.audio.play(pending.emitterId)
    }
  }

  start(): void {
    this.startedAt = performance.now()
    this.timer = window.setInterval(() => this.step(), TICK_MS)
  }

  stop(): void {
    window.clearInterval(this.timer)
    window.clearTimeout(this.captionTimer)
    this.stopSensors()
    this.audio.stopAll()
  }

  /** Drops the listener at a spot on the map. Backs Test Drive and the simulator. */
  moveTo(position: Vec2): void {
    this.positionMode = 'manual'
    this.autopilot = undefined
    this.replay = undefined
    this.rawPosition = position
    this.filteredPosition = this.withNoise(position, performance.now())
    this.accuracyM = 0
    this.accuracyDegraded = false
  }

  face(headingDeg: number): void {
    this.headingDeg = ((headingDeg % 360) + 360) % 360
    if (this.positionMode === 'manual') this.headingSource = 'manual'
  }

  /** Switches to the phone's own sensors. */
  async useGps(): Promise<void> {
    if (!('geolocation' in navigator)) throw new Error('This device cannot report its location.')
    // Toggling Test Drive off and on again would otherwise leave a live watch
    // and a second set of orientation listeners behind on every pass.
    this.stopSensors()
    this.positionMode = 'gps'
    this.filter.reset()

    this.geolocationWatch = navigator.geolocation.watchPosition(
      (fix) => this.onGeolocation(fix),
      () => {
        this.headingSource = 'none'
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15_000 },
    )

    await this.requestCompass()
  }

  useManualPositioning(): void {
    this.positionMode = 'manual'
    this.stopSensors()
    this.headingSource = 'manual'
  }

  /**
   * Releases the sensors. The compass matters as much as the watch: left
   * attached, it keeps writing a heading and quietly overrides the one the
   * tester just set with the Test Drive dial.
   */
  private stopSensors(): void {
    if (this.geolocationWatch !== undefined) {
      navigator.geolocation.clearWatch(this.geolocationWatch)
      this.geolocationWatch = undefined
    }
    window.removeEventListener('deviceorientationabsolute', this.onOrientation, true)
    window.removeEventListener('deviceorientation', this.onOrientation, true)
  }

  private async requestCompass(): Promise<void> {
    const orientation = window.DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>
    }
    try {
      if (typeof orientation?.requestPermission === 'function') {
        const outcome = await orientation.requestPermission()
        if (outcome !== 'granted') return
      }
      window.addEventListener('deviceorientationabsolute', this.onOrientation, true)
      window.addEventListener('deviceorientation', this.onOrientation, true)
    } catch {
      // No compass is survivable: heading falls back to direction of travel.
    }
  }

  private readonly onOrientation = (event: DeviceOrientationEvent): void => {
    if (this.positionMode !== 'gps') return
    const webkitHeading = (event as unknown as { webkitCompassHeading?: number })
      .webkitCompassHeading
    if (typeof webkitHeading === 'number' && Number.isFinite(webkitHeading)) {
      this.headingDeg = webkitHeading
      this.headingSource = 'compass'
      return
    }
    if (event.absolute && typeof event.alpha === 'number') {
      // alpha counts anticlockwise from north; a compass bearing does not.
      this.headingDeg = (360 - event.alpha) % 360
      this.headingSource = 'compass'
    }
  }

  private onGeolocation(fix: GeolocationPosition): void {
    if (!this.placement || this.positionMode !== 'gps') return

    const world = geoToWorld(this.placement, {
      lat: fix.coords.latitude,
      lon: fix.coords.longitude,
    })
    this.rawPosition = world
    this.accuracyM = fix.coords.accuracy

    const filtered = this.filter.update(world, fix.timestamp)
    if (filtered.rejected) this.rejectedFixes += 1
    this.filteredPosition = filtered.position

    if (typeof fix.coords.speed === 'number' && Number.isFinite(fix.coords.speed)) {
      this.speedMps = Math.max(0, fix.coords.speed)
    }

    // Falling back to direction of travel is only honest while actually
    // moving; standing still, a course-over-ground heading is noise.
    if (
      this.headingSource !== 'compass' &&
      typeof fix.coords.heading === 'number' &&
      Number.isFinite(fix.coords.heading) &&
      this.speedMps > 1
    ) {
      this.headingDeg = fix.coords.heading
      this.headingSource = 'courseOverGround'
    }

    this.accuracyDegraded = fix.coords.accuracy > this.definition.accuracyFloorM
  }

  private step(): void {
    const nowMs = performance.now() - this.startedAt
    const deltaSeconds = this.lastStepAt === 0 ? 0 : (nowMs - this.lastStepAt) / 1000
    this.lastStepAt = nowMs
    this.advanceSimulation(nowMs, deltaSeconds)

    for (const emitterId of this.paths.advance(this.mixWorld.emitters, deltaSeconds)) {
      this.audio.stop(emitterId, 250)
      this.runtimes.set(emitterId, { ...this.runtimes.get(emitterId), playing: false })
    }

    const emitterPositions = new Map<string, Vec2>()
    for (const emitter of this.mixWorld.emitters) {
      const path = this.paths.stateOf(emitter.id)
      if (!path) continue
      emitterPositions.set(emitter.id, path.position)
      const runtime = this.runtimes.get(emitter.id)
      if (runtime?.playing) {
        this.runtimes.set(emitter.id, {
          ...runtime,
          position: path.position,
          velocityMps: path.velocityMps,
        })
      }
    }
    const position = this.filteredPosition
    const worldHeading = this.placement
      ? worldHeadingFromTrueNorth(this.headingDeg, this.placement)
      : this.headingDeg

    const pose: Pose | undefined = position
      ? {
          t: nowMs,
          position,
          headingDeg: worldHeading,
          speedMps: this.speedMps,
          accuracyM: this.accuracyM,
          source: this.positionMode === 'gps' ? 'gps' : 'simulated',
        }
      : undefined

    const context: TickContext = {
      nowMs,
      pose,
      previousPose: this.previousPose,
      accuracyDegraded: this.accuracyDegraded,
      roles: ['listener'],
      emitterPositions,
    }

    const result = tick(this.compiled, this.engine, context)
    this.previousPose = pose

    for (const firing of result.firings) {
      const rule = this.compiled.rulesById.get(firing.ruleId)
      this.events.onLog?.({ ts: Date.now(), text: `Rule fired: ${rule?.name ?? firing.ruleId}` })
      // The log is the record of the whole world, not just the server's half.
      this.socket?.sendClientEvent(firing.ruleId, rule?.triggers[0]?.type ?? 'session', firing.atMs)
    }

    for (const dispatch of result.dispatched) this.performAction(dispatch.action)
    this.drainPendingActions()
    this.drainPendingReplays()
    this.sweepTransients()

    if (pose) {
      const decision = computeMix(
        this.mixWorld,
        this.mixState,
        { pose, headingDeg: worldHeading, busTrimDb: this.busTrimDb },
        this.runtimes,
      )
      this.lastMix = decision
      this.audio.applyMix(decision)
      this.events.onMix?.(decision)

      for (const emitterId of decision.virtualized) {
        this.events.onLog?.({ ts: Date.now(), text: `Voice culled: ${emitterId}` })
      }

      if (Date.now() - this.lastPoseUpload >= POSE_UPLOAD_MS) {
        this.lastPoseUpload = Date.now()
        this.socket?.sendPose(pose)
        this.socket?.sendHealth({
          accuracyM: this.accuracyM,
          headingSource: this.headingSource,
          activeVoices: decision.voices.filter((voice) => voice.audible).length,
          culledVoices: decision.voices.filter((voice) => !voice.audible).length,
          rejectedFixes: this.rejectedFixes,
          clientVersion: 'web-0.1.0',
        })
      }
    }

    this.events.onDiagnostics?.(this.diagnostics())
  }

  diagnostics(): Diagnostics {
    return {
      positionMode: this.positionMode,
      headingSource: this.headingSource,
      accuracyM: this.accuracyM,
      accuracyDegraded: this.accuracyDegraded,
      speedMps: this.speedMps,
      rawPosition: this.rawPosition,
      filteredPosition: this.filteredPosition,
      headingDeg: this.headingDeg,
      activeVoices: this.lastMix?.voices.filter((voice) => voice.audible).length ?? 0,
      culledVoices: this.lastMix?.voices.filter((voice) => !voice.audible).length ?? 0,
      rejectedFixes: this.rejectedFixes,
      clockOffsetMs: this.socket?.clockOffsetMs ?? 0,
      connected: this.socket?.connected ?? false,
      inSafetyZone: this.lastMix?.inSafetyZone ?? false,
    }
  }

  /** Actions arriving from the server's half of the rule engine. */
  performServerActions(
    actions: { action: { type: string; [k: string]: unknown }; atMs: number }[],
  ): void {
    for (const entry of actions) this.performAction(entry.action as Action)
  }

  private drainPendingActions(): void {
    const now = Date.now()
    while (this.pendingActions.length > 0 && this.pendingActions[0].dueAt <= now) {
      const next = this.pendingActions.shift()
      if (next) this.applyAction(next.action)
    }
  }

  private performAction(action: Action): void {
    if (action.delayMs > 0) {
      this.pendingActions.push({ action, dueAt: Date.now() + action.delayMs })
      this.pendingActions.sort((a, b) => a.dueAt - b.dueAt)
      return
    }
    this.applyAction(action)
  }

  private applyAction(action: Action): void {
    switch (action.type) {
      case 'play':
        this.runtimes.set(action.emitterId, {
          ...this.runtimes.get(action.emitterId),
          playing: true,
        })
        this.audio.play(action.emitterId, { fromOffsetMs: action.fromOffsetMs })
        this.showCaptionFor(action.emitterId)
        break

      case 'stop':
        this.runtimes.set(action.emitterId, {
          ...this.runtimes.get(action.emitterId),
          playing: false,
        })
        this.audio.stop(action.emitterId, action.fadeMs)
        break

      case 'setPlayback':
        this.audio.setPaused(action.emitterId, action.state === 'pause')
        break

      case 'crossfade':
        this.applyAction({ type: 'stop', emitterId: action.fromEmitterId, fadeMs: action.overMs, delayMs: 0 })
        this.applyAction({ type: 'play', emitterId: action.toEmitterId, fromOffsetMs: 0, delayMs: 0 })
        break

      case 'showCard':
        this.events.onCard?.({
          title: action.title,
          body: action.body,
          durationMs: action.durationMs,
        })
        break

      case 'moveEmitter': {
        const emitter = this.mixWorld.emitters.find((candidate) => candidate.id === action.emitterId)
        if (emitter?.kind === 'point') emitter.position = action.to
        break
      }

      case 'pathControl': {
        if (action.command === 'stop') {
          this.paths.stop(action.emitterId)
          this.runtimes.set(action.emitterId, {
            ...this.runtimes.get(action.emitterId),
            playing: false,
          })
          this.audio.stop(action.emitterId, 400)
          break
        }
        if (action.command === 'restart') this.paths.restart(action.emitterId, action.speedMps)
        else this.paths.start(action.emitterId, action.speedMps)
        this.runtimes.set(action.emitterId, {
          ...this.runtimes.get(action.emitterId),
          playing: true,
        })
        this.audio.play(action.emitterId)
        break
      }

      case 'spawnOneShot':
        this.spawnOneShot(action)
        break

      case 'fadeTo': {
        const target = action.target
        if (target.kind === 'bus') {
          // Recorded as a trim so the next mix carries it; writing it onto the
          // graph directly would be overwritten by that same mix.
          this.setBusTrim(target.busId, action.toGainDb)
          break
        }
        // Gain is read from the emitter every frame, so changing it here is
        // enough for the next mix to pick it up.
        const faded = this.mixWorld.emitters.find((candidate) => candidate.id === target.emitterId)
        if (faded) faded.gainDb = action.toGainDb
        break
      }

      case 'setVariable':
      case 'setRuleEnabled':
        // Already applied inside the engine before reaching here; they are
        // passed on only so the log and a replay see them.
        break
    }
  }

  /**
   * A sound with no authored emitter behind it — the operator dropping a
   * whisper two metres behind a particular guest, or a twig snapping somewhere
   * in the woods.
   *
   * Added to the world as a temporary emitter rather than played flat, so it
   * gets the same distance, direction and culling as everything else, and
   * removed once it has finished.
   */
  private spawnOneShot(action: Extract<Action, { type: 'spawnOneShot' }>): void {
    const listener = this.filteredPosition
    if (!listener) return

    let position: Vec2
    if (action.at.kind === 'point') {
      position = action.at.position
    } else {
      // Listener-relative offsets turn with the listener, so "behind you" is
      // behind you and not merely south.
      // The device reports against true north; the world has its own north.
      const worldHeading = this.placement
        ? worldHeadingFromTrueNorth(this.headingDeg, this.placement)
        : this.headingDeg
      const heading = (worldHeading * Math.PI) / 180
      const forward = { x: Math.sin(heading), y: Math.cos(heading) }
      const right = { x: forward.y, y: -forward.x }
      position = {
        x: listener.x + forward.x * action.at.forwardM + right.x * action.at.rightM,
        y: listener.y + forward.y * action.at.forwardM + right.y * action.at.rightM,
      }
    }

    const id = `oneshot-${Math.random().toString(36).slice(2, 10)}`
    const emitter = EmitterSchema.parse({
      kind: 'point',
      id,
      name: 'One-shot',
      source: { kind: 'asset', assetId: action.assetId },
      busId: action.busId,
      gainDb: action.gainDb,
      position,
      distance: { refDistanceM: 4, maxDistanceM: 120 },
    })

    this.transients.push(emitter)
    ;(this.mixWorld.emitters as Emitter[]).push(emitter)
    this.runtimes.set(id, { playing: true })
    // Register before playing: the audio runtime resolves the source from its
    // own emitter table, and an unknown id is silently ignored.
    this.audio.registerEmitter(emitter)
    this.audio.play(id)
  }

  /** Drops one-shots that have finished, so the world does not accumulate them. */
  private sweepTransients(): void {
    for (let i = this.transients.length - 1; i >= 0; i--) {
      const emitter = this.transients[i]
      if (this.audio.isPlaying(emitter.id)) continue

      this.transients.splice(i, 1)
      this.runtimes.delete(emitter.id)
      this.audio.unregisterEmitter(emitter.id)
      const emitters = this.mixWorld.emitters as Emitter[]
      const index = emitters.findIndex((candidate) => candidate.id === emitter.id)
      if (index >= 0) emitters.splice(index, 1)
    }
  }
}
